import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { API } from "../../config/config";
import { TenantData, TenantState } from "../../types/types";

const initialState: TenantState = {
  loading: false,
  success: false,
  error: null,
  tenants: [],
};

export const addTenant = createAsyncThunk(
  "tenant/addTenant",
  async (tenantData: TenantData, { rejectWithValue }) => {
    try {
      const response = await API.POST("/auth/signup", tenantData);
      console.log({ response });
      return response.data;
    } catch (error: any) {
      console.log({ error });
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTenants = createAsyncThunk(
  "tenant/fetchTenants",
  async (
    {
      building,
      role,
      status,
      ownerId,
      flatNo,
    }: {
      building?: string;
      role: string;
      status?: string;
      ownerId?: string;
      flatNo?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      if (!role) {
        throw new Error("Both `role` and `status` are required parameters.");
      }

      const url = `/users?role=${role}${
        building ? `&building=${building}` : ""
      }${ownerId ? `&ownerId=${ownerId}` : ""}${
        flatNo ? `&flatNo=${flatNo}` : ""
      }${status ? `&status=${status}` : ""}`;

      const response = await API.GET(url);
      console.log({ response });
      return response.data.data;
    } catch (error: any) {
      console.log({ error });
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateTenant = createAsyncThunk(
  "tenant/updateTenant",
  async (
    { id, payload }: { id: string; payload: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await API.PUT(`/users/${id}`, payload);
      console.log({ response });
      return response.data;
    } catch (error: any) {
      console.log({ error });
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const tenantSlice = createSlice({
  name: "tenant",
  initialState,
  reducers: {
    resetTenantState: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addTenant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTenant.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(addTenant.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(fetchTenants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTenants.fulfilled,
        (state, action: PayloadAction<TenantData[]>) => {
          state.loading = false;
          state.success = true;
          state.tenants = action.payload;
          state.error = null;
        }
      )
      .addCase(fetchTenants.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(updateTenant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTenant.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateTenant.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      });
  },
});

export const { resetTenantState } = tenantSlice.actions;
export default tenantSlice.reducer;
